import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GridColTypeDef,
  GridColDef,
  GridColumnMenuProps,
  GridValueFormatter,
} from '@mui/x-data-grid-pro';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import { EDListItemButton } from '../../../../../../../BaseComponents/List/Components/EDListItem';
import EDList from '../../../../../../../BaseComponents/List/EDList';
import EDDataGrid from '../../../../../../../BaseComponents/XGrid/EDDataGrid';
import mergeSort, { numberCompare } from '../../../../../../../Utils/mergeSort';
import { EDTextEdit } from '../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { IFormData } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { IClientAssociationModel } from '../../../../../../../Types/ClientModelTypes';
import { ModelContext } from '../../../../../../../Model/ModelContext/ModelContext';
import { findCollection } from '../../../../../../../Model/avenyModelUtils';
import {
  convertImportedRows,
  convertToInternalSelectableColumnHeader,
  getAssignedSelectableColumnHeaders,
  parseClipboard,
} from './Utils/importDialogUtils';
import classNames from 'classnames';
import { EDDataGridClasses } from '../../../../../../../BaseComponents/XGrid/EdDataGridUtils';
import { debounce } from 'debounce';
import { DEBOUNCE_DELAY_TIME } from '../../../../../../../Const/SystemWideConsts';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';

export interface ISelectableColumnHeader {
  id: string;
  customLabel?: string;
  attribute: string | 'Kodsträng';
  associationName?: string;
  associationPath?: string[];
  isCellValueValid?: isCellValueValidFunction;
  customColumnOptions?: GridColTypeDef;
}

export interface IParseClipboardValue {
  id: string;
  index: number;
}

export interface IConvertedClipboardValue extends IParseClipboardValue {
  [key: string]: any;
  collectionData: IFormData;
}

export interface IIsCellValueValidResult {
  validValue: boolean;
  errorMessage: string;
}
export type isCellValueValidFunction = (
  cellValue: string,
  row: any,
  columnInfo: IInternalSelectableColumnHeader
) => IIsCellValueValidResult;

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectableColumnHeaders: ISelectableColumnHeader[];
  onCreateCallback: (chosenRows: IConvertedClipboardValue[]) => void;
  collectionName: string;
  associationName?: string;
  invalidValuesInfoText?: string;
}

export interface IInternalSelectableColumnHeader
  extends ISelectableColumnHeader {
  label: string;
  valueFormatter?: GridValueFormatter<any, any, any, never>;
}

export interface IAssignedSelectableColumnHeader
  extends IInternalSelectableColumnHeader {
  selectedForIndex: number | null;
}

interface ICustomColumnMenuProps extends GridColumnMenuProps {
  assignedColumnHeaders: IAssignedSelectableColumnHeader[];
  columns: GridColDef[];
  selectableColumnHeaders: IInternalSelectableColumnHeader[];
  setAssignedColumnHeaders: React.Dispatch<
    React.SetStateAction<IAssignedSelectableColumnHeader[]>
  >;
  step: number;
}

const CustomColumnMenu = React.forwardRef<
  HTMLUListElement,
  ICustomColumnMenuProps
>((props: ICustomColumnMenuProps, ref) => {
  return (
    <EDList>
      {props.selectableColumnHeaders.map((buttonColumnHeader, index) => {
        return (
          <EDListItemButton
            key={index}
            onClick={event => {
              const assignedColumnHeader = props.assignedColumnHeaders.find(
                assignedColumnHeader =>
                  assignedColumnHeader.selectedForIndex ===
                  parseInt(props.colDef.field)
              );

              if (assignedColumnHeader == undefined) {
                //Om vald header redan är vald i en annan kolumn => Filtrera bort den
                const newAssignedHeaders = props.assignedColumnHeaders.filter(
                  assignedColumnHeader =>
                    assignedColumnHeader.label != buttonColumnHeader.label &&
                    assignedColumnHeader.selectedForIndex !=
                      parseInt(props.colDef.field)
                );

                const newColumnHeader: IAssignedSelectableColumnHeader = {
                  ...buttonColumnHeader,
                  selectedForIndex: parseInt(props.colDef.field),
                };

                props.setAssignedColumnHeaders([
                  ...newAssignedHeaders,
                  newColumnHeader,
                ]);
              } else if (assignedColumnHeader) {
                //Om vald header redan är vald i en annan kolumn => Filtrera bort den
                const newAssignedHeaders = props.assignedColumnHeaders.filter(
                  assignedColumnHeader =>
                    assignedColumnHeader.label != buttonColumnHeader.label &&
                    assignedColumnHeader.selectedForIndex !=
                      parseInt(props.colDef.field)
                );

                if (assignedColumnHeader.label === buttonColumnHeader.label) {
                  props.setAssignedColumnHeaders([...newAssignedHeaders]);
                } else {
                  const newColumnHeader: IAssignedSelectableColumnHeader = {
                    ...buttonColumnHeader,
                    selectedForIndex: parseInt(props.colDef.field),
                  };

                  props.setAssignedColumnHeaders([
                    ...newAssignedHeaders,
                    newColumnHeader,
                  ]);
                }
              }

              props.hideMenu(event);
            }}
          >
            <EDTypography>{buttonColumnHeader.label}</EDTypography>
          </EDListItemButton>
        );
      })}
    </EDList>
  );
});

const kontodelRegex = /\bKontodel\d+\b/;

const ImportDialog: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { avenyCollectionMap } = useContext(ModelContext);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const numberOfTitleRowsRef = useRef<HTMLInputElement>(null);

  const [pasteValue, setPasteValue] =
    useState<{ id: string; index: number }[]>();
  const [convertedValues, setConvertedValues] =
    useState<IParseClipboardValue[]>();
  const [assignedColumnHeaders, setAssignedColumnHeaders] = useState<
    IAssignedSelectableColumnHeader[]
  >([]);
  const [numberOfTitleRows, setNumberOfTitleRows] = useState<
    number | undefined
  >();
  const [step, setStep] = useState<number>(1);

  const [chosenRows, setChosenRows] = useState<
    { id: string; index: number; collectionData: IFormData }[]
  >([]);

  const collection = useMemo(
    () => findCollection(avenyCollectionMap, props.collectionName),
    [avenyCollectionMap, props.collectionName]
  );
  const association: IClientAssociationModel | undefined = useMemo(
    () =>
      props.associationName && collection
        ? collection.Associations[props.associationName]
        : undefined,
    [collection, props.associationName]
  );

  const associatedCollection = useMemo(
    () =>
      association &&
      findCollection(avenyCollectionMap, association.AssociatedCollectionName),
    [association, avenyCollectionMap]
  );

  const currentCollection = useMemo(
    () => (associatedCollection ? associatedCollection : collection),
    [associatedCollection, collection]
  );

  if (!currentCollection) {
    throw new Error(
      props.associationName
        ? 'Cannot find the collection with the name: ' + props.associationName
        : 'Cannot find the collection with the name: ' + props.collectionName
    );
  }

  const internalSelectableColumnHeaders: IInternalSelectableColumnHeader[] =
    useMemo(
      () =>
        props.selectableColumnHeaders.map(header =>
          convertToInternalSelectableColumnHeader(
            header,
            currentCollection.CollectionName,
            avenyCollectionMap
          )
        ),
      [
        avenyCollectionMap,
        currentCollection.CollectionName,
        props.selectableColumnHeaders,
      ]
    );

  const onCheckboxClick = useCallback(
    (rowID: string) => (isChecked: boolean) => {
      if (isChecked) {
        const addedRow = (
          convertedValues as
            | { id: string; index: number; collectionData: IFormData }[]
            | undefined
        )?.find(row => row.id === rowID.toString());

        if (addedRow) {
          setChosenRows([...chosenRows, addedRow]);
        }
      } else {
        const newRows = chosenRows.filter(row => row.id !== rowID.toString());
        setChosenRows([...newRows]);
      }
    },
    [chosenRows, setChosenRows, convertedValues]
  );

  const onPasteClick = useCallback(() => {
    navigator.clipboard.readText().then(text => {
      const rows: IParseClipboardValue[] = parseClipboard(text);
      setPasteValue(rows);
      setTimeout(() => numberOfTitleRowsRef.current?.focus(), 10);
    });
  }, []);

  const gridColumns = useMemo(() => {
    const columns: GridColDef[] = [];

    if (pasteValue && step === 1) {
      const firstPastedRow = pasteValue?.[0] ?? {};
      const numberOfColumns = Object.keys(firstPastedRow)?.filter(
        key => key != 'id' && key != 'index'
      );

      const columns = numberOfColumns.map((key, index) => {
        const assignedHeader = assignedColumnHeaders.find(
          columnHeader => columnHeader.selectedForIndex === parseInt(key)
        );

        const column: GridColDef = {
          field: key,
          headerName: assignedHeader
            ? assignedHeader.label
            : 'Kolumn ' + (1 + parseInt(key)),
          sortable: false,
          headerClassName:
            assignedColumnHeaders.find(
              header => header.selectedForIndex === index
            ) === undefined
              ? EDDataGridClasses.importDialogCellInvalidValue
              : undefined,
        };

        return column;
      });

      return columns;
    }

    if (step === 2) {
      const checkboxColumn: GridColDef = {
        align: 'center',
        field: 'Vald',
        renderCell: params => {
          return (
            <EDCheckbox
              checked={chosenRows.some(row => row.id === params.id.toString())}
              onCheck={onCheckboxClick(params.id.toString())}
            />
          );
        },
        minWidth: 46,
      };

      const errorColumn: GridColDef = {
        field: 'ErrorMessage',
        headerName: 'Felmeddelanden',
      };

      const hasKodstrangColumn = assignedColumnHeaders.some(
        columnHeader => columnHeader.attribute === 'Kodsträng'
      );

      const invalidValueClassName = classNames(
        EDDataGridClasses.importDialogCellInvalidValue
      );

      if (hasKodstrangColumn) {
        const columnNames = convertedValues?.reduce((prev, current) => {
          const newKeys = Object.keys(current).reduce<string[]>(
            (prevKeys, key) => {
              if (key.includes('.')) {
                const separatedKeyParts = key.split('.');

                const extractedKontodelKey =
                  separatedKeyParts.find(part => {
                    return kontodelRegex.test(part);
                  }) ?? '';

                if (
                  extractedKontodelKey.includes('Kontodel') &&
                  !prevKeys.includes(extractedKontodelKey)
                ) {
                  return [...prevKeys, extractedKontodelKey];
                }
                return prevKeys;
              }

              if (kontodelRegex.test(key) && !prevKeys.includes(key)) {
                return [...prevKeys, key];
              }
              return prevKeys;
            },
            prev
          );

          return newKeys;
        }, [] as string[]);

        const newColumns = columnNames?.reduce(
          (accumulatedColumns, columnName, index) => {
            const internalHeader: IInternalSelectableColumnHeader | undefined =
              internalSelectableColumnHeaders.find(header => {
                return (
                  header.associationName === columnName ||
                  header.attribute === columnName
                );
              });

            if (internalHeader) {
              const alreadyExists = accumulatedColumns.find(col => {
                return (
                  col.associationName === internalHeader.associationName &&
                  col.attribute === internalHeader.attribute
                );
              });

              if (!alreadyExists) {
                return [
                  ...accumulatedColumns,
                  {
                    ...internalHeader,
                    selectedForIndex: assignedColumnHeaders.length + index,
                  },
                ];
              }
            }

            return accumulatedColumns;
          },
          [] as IAssignedSelectableColumnHeader[]
        );

        const filteredNewColumns = newColumns?.filter(column => {
          return !assignedColumnHeaders.some(assignedColumn => {
            return (
              assignedColumn.attribute === column.attribute &&
              assignedColumn.associationName === column.associationName
            );
          });
        });

        const filteredAssignedHeaders = assignedColumnHeaders.filter(column => {
          return column.attribute !== 'Kodsträng';
        });

        const step2Columns = mergeSort(
          filteredNewColumns
            ? [...filteredAssignedHeaders, ...filteredNewColumns]
            : [...assignedColumnHeaders],
          (a, b) => {
            return numberCompare(
              a.selectedForIndex ?? 0,
              b.selectedForIndex ?? 0
            );
          }
        ).map(columnHeader =>
          getAssignedSelectableColumnHeaders(
            columnHeader,
            invalidValueClassName
          )
        );

        return [checkboxColumn, ...step2Columns, errorColumn];
      }

      const step2Columns = mergeSort([...assignedColumnHeaders], (a, b) => {
        return numberCompare(a.selectedForIndex ?? 0, b.selectedForIndex ?? 0);
      }).map(columnHeader =>
        getAssignedSelectableColumnHeaders(columnHeader, invalidValueClassName)
      );

      return [checkboxColumn, ...step2Columns, errorColumn];
    }
    return columns;
  }, [
    assignedColumnHeaders,
    chosenRows,
    convertedValues,
    internalSelectableColumnHeaders,
    onCheckboxClick,
    pasteValue,
    step,
  ]);

  const debounceSetColumnHeaders = debounce((numberOfRows: number) => {
    if (numberOfRows > 0) {
      const titleRows =
        pasteValue?.slice(0, numberOfRows)?.map(row => Object.values(row)) ??
        [];

      const newAssignedHeaders = titleRows?.reduce(
        (prevAssignedColumnHeaders, currentTitleRow) => {
          const newSelectableColumnHeaders = currentTitleRow.reduce(
            (prevColumns, title, index) => {
              const indexAlreadyAssigned = prevAssignedColumnHeaders.some(
                previousColumnHeader => {
                  return previousColumnHeader.selectedForIndex === index;
                }
              );

              if (indexAlreadyAssigned || typeof title !== 'string') {
                return prevColumns;
              }

              const column = internalSelectableColumnHeaders.find(
                header =>
                  title.trim().toLowerCase() === header.label.toLowerCase()
              );

              if (column) {
                const newColumnHeader: IAssignedSelectableColumnHeader = {
                  ...column,
                  selectedForIndex: index,
                };

                return [...prevColumns, newColumnHeader];
              }

              return prevColumns;
            },
            [] as IAssignedSelectableColumnHeader[]
          );

          if (newSelectableColumnHeaders.length > 0) {
            return [
              ...prevAssignedColumnHeaders,
              ...newSelectableColumnHeaders,
            ];
          }

          return prevAssignedColumnHeaders;
        },
        [] as IAssignedSelectableColumnHeader[]
      );
      setAssignedColumnHeaders(newAssignedHeaders);
    }
  }, DEBOUNCE_DELAY_TIME);

  const resetStates = useCallback(() => {
    setPasteValue(undefined);
    setConvertedValues(undefined);
    setAssignedColumnHeaders([]);
    setNumberOfTitleRows(undefined);
    setStep(1);
    setChosenRows([]);
  }, []);

  const onClose = () => {
    props.setOpen(false);
  };

  return (
    <EDDialog
      open={props.open}
      onClose={onClose}
      onEntered={() => inputRef?.current?.focus()}
      onExited={() => resetStates()}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: onClose,
        },
        {
          label: 'Börja om',
          onClick: () => {
            resetStates();
          },
        },
        ...(step === 2
          ? [
              {
                label: 'Tillbaka',
                onClick: () => {
                  setConvertedValues(undefined);
                  setStep(1);
                },
              },
            ]
          : []),
        ...(pasteValue != undefined
          ? [
              {
                label: step === 1 ? 'Nästa' : 'Använd',
                buttonProps: {
                  disabled:
                    (step === 1 && assignedColumnHeaders.length === 0) ||
                    pasteValue === undefined,
                  disabledReason:
                    assignedColumnHeaders.length === 0
                      ? 'Ange rubrik på minst en kolumn för att fortsätta'
                      : pasteValue === undefined
                      ? 'Klistra in värden för att fortsätta'
                      : undefined,
                },
                onClick: () => {
                  if (step === 1) {
                    const rows = pasteValue?.slice(numberOfTitleRows) ?? [];

                    const filteredConvertedRows = mergeSort(
                      convertImportedRows(
                        rows,
                        assignedColumnHeaders,
                        internalSelectableColumnHeaders
                      ),
                      (a, b) => a.index - b.index
                    );

                    setConvertedValues(filteredConvertedRows);
                    setChosenRows(
                      filteredConvertedRows?.filter(
                        row =>
                          row?.ErrorColumns == undefined &&
                          row?.ErrorMessage == undefined
                      ) ?? []
                    );

                    setStep(2);
                  } else if (step === 2) {
                    const sortedRows = mergeSort(
                      chosenRows,
                      (a, b) => a.index - b.index
                    );

                    props.onCreateCallback(sortedRows);
                    props.setOpen(false);
                    resetStates();
                  }
                },
              },
            ]
          : []),
      ]}
      fullHeight
      fullWidth
      maxWidth={'lg'}
      title="Import av rader från urklipp"
    >
      {pasteValue ? (
        <>
          {step === 1 && (
            <EDBox sx={{ display: 'flex', flexFlow: 'column nowrap' }}>
              <EDBox mx={1}>
                <EDTextEdit
                  id="numberOfTitleRows"
                  label="Antal rubrikrader"
                  inputRef={numberOfTitleRowsRef}
                  onBlur={(event: React.FocusEvent<HTMLInputElement | HTMLDivElement>) => {
                    const value =
                      event.currentTarget instanceof HTMLInputElement
                        ? event.currentTarget.value
                        : '';
                    const numberOfRows = parseInt(value);
                    if (
                      numberOfTitleRows == undefined ||
                      Number.isNaN(numberOfRows)
                    ) {
                      setNumberOfTitleRows(0);
                    }
                  }}
                  onChange={newValue => {
                    const numberOfRows = parseInt(newValue);
                    if (!isNaN(numberOfRows)) {
                      setNumberOfTitleRows(numberOfRows ?? undefined);
                      debounceSetColumnHeaders(numberOfRows);
                    } else {
                      setNumberOfTitleRows(undefined);
                    }
                  }}
                  fullWidth={false}
                  size={'small'}
                  value={numberOfTitleRows?.toString() ?? ''}
                />
              </EDBox>
            </EDBox>
          )}
          {step === 2 && (
            <EDBox
              sx={[
                {
                  display: 'flex',
                  flexFlow: 'row nowrap',
                  alignItems: 'center',
                },
                Boolean(props.invalidValuesInfoText) && {
                  justifyContent: 'space-between',
                },
              ]}
            >
              <EDCheckbox
                checkboxSpacing={{ my: 2 }}
                indeterminate={
                  chosenRows.length > 0
                    ? chosenRows.length === convertedValues?.length
                      ? false
                      : true
                    : false
                }
                checked={convertedValues?.length === chosenRows.length}
                onCheck={isInputChecked => {
                  if (isInputChecked) {
                    const addedRow = convertedValues as
                      | {
                          id: string;
                          index: number;
                          collectionData: IFormData;
                        }[]
                      | undefined;

                    if (addedRow) {
                      setChosenRows(addedRow);
                    }
                  } else {
                    setChosenRows([]);
                  }
                }}
                label={'Välj alla'}
              />
              {Boolean(props.invalidValuesInfoText) && (
                <EDTypography italic>
                  <EDIcon iconType="info" px={1} />
                  {props.invalidValuesInfoText}
                </EDTypography>
              )}
            </EDBox>
          )}
          <EDBox
            sx={{
              display: 'flex',
              flexFlow: 'column nowrap',
              height: 'calc(100% - (42px + 48px))',
            }}
          >
            <EDBox sx={{ height: '100%' }}>
              {step === 1 ? (
                <EDDataGrid
                  columns={gridColumns}
                  rows={pasteValue}
                  ColumnMenu={CustomColumnMenu as React.FunctionComponent<React.PropsWithChildren<unknown>>}
                  disableColumnMenu={step !== 1}
                  disableColumnReorder
                  disableMultipleColumnsSorting
                  columnMenuProps={{
                    assignedColumnHeaders,
                    columns: gridColumns,
                    selectableColumnHeaders: internalSelectableColumnHeaders,
                    setAssignedColumnHeaders,
                    step,
                  }}
                  ColumnMenuIcon={() => {
                    return <EDIcon iconType="arrow_drop_down" />;
                  }}
                />
              ) : (
                convertedValues && (
                  <EDDataGrid
                    columns={gridColumns}
                    disableColumnMenu
                    disableColumnReorder
                    disableMultipleColumnsSorting
                    rows={convertedValues}
                  />
                )
              )}
            </EDBox>
          </EDBox>
        </>
      ) : (
        <EDBox>
          <EDTypography>Klistra in värdena i textfältet nedan:</EDTypography>
          <textarea
            ref={inputRef}
            style={{ width: '100%', height: '56px', resize: 'vertical' }}
            readOnly
            onPaste={event => {
              event.preventDefault();

              const clipboardData = event.clipboardData.getData('Text');

              const rows = parseClipboard(clipboardData);

              setPasteValue(rows);
              setTimeout(() => numberOfTitleRowsRef.current?.focus(), 10);
            }}
            value="Klistra in värdena här"
          />
          <EDBox sx={{ display: 'flex', justifyContent: 'end', marginTop: 1 }}>
            <EDButton onClick={onPasteClick}>Klistra in från urklipp</EDButton>
          </EDBox>
        </EDBox>
      )}
    </EDDialog>
  );
};

export default ImportDialog;
